// InvoiceEditDialog — редактирование счёта (V14 W6, §3.4).
// PATCH /api/invoices/{id}: amount/dueDate (amount — только DRAFT, OQ-7) + expectedUpdatedAt;
// кнопка «Выставить счёт» — lifecycle-переход DRAFT→OPEN (после OPEN сумма фиксируется).
// Используется из строки списка счетов для DRAFT-счёта.

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { invoicesApi, type Invoice } from '@/api/invoices';
import { parseAmount } from '@/lib/money';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';

interface InvoiceEditDialogProps {
  open: boolean;
  onClose: () => void;
  /** Редактируемый счёт (DRAFT — правка суммы/срока + выставление). */
  invoice: Invoice;
  /** Колбэк успешного сохранения/выставления (например, бейдж/refetch). */
  onSaved?: (invoice: Invoice) => void;
}

/** Диалог редактирования счёта: сумма/срок (DRAFT) + «Выставить счёт» (DRAFT→OPEN). */
export function InvoiceEditDialog({ open, onClose, invoice, onSaved }: InvoiceEditDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState(String(invoice.amount));
  const [dueDate, setDueDate] = useState('');

  // Сброс формы под диалог при каждом открытии (канон PaymentNewDialog).
  useEffect(() => {
    if (open) {
      setAmount(String(invoice.amount));
      setDueDate('');
    }
  }, [open, invoice]);

  const parsedAmount = parseAmount(amount);
  const amountInvalid = amount.trim() !== '' && (parsedAmount === null || parsedAmount < 0);
  const canSubmit = !amountInvalid;

  /** Общий хелпер PATCH: либо правка полей, либо lifecycle-переход (канон PaymentService). */
  const saveMutation = useMutation({
    mutationFn: (payload: { status?: 'OPEN'; amount?: number; dueDate?: string }) =>
      invoicesApi.update(invoice.id, { ...payload, expectedUpdatedAt: invoice.updatedAt }),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      onSaved?.(updated);
      onClose();
    },
  });

  const isPending = saveMutation.isPending;
  const saveError = saveMutation.isError ? (
    <Alert severity="error">{extractApiErrorMessage(saveMutation.error, t('invoices.edit.error'))}</Alert>
  ) : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('invoices.edit.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {saveError}
          <TextField
            label={t('invoices.amount')}
            fullWidth
            value={amount}
            error={amountInvalid}
            helperText={amountInvalid ? t('invoices.amountInvalid') : undefined}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ 'aria-label': t('invoices.amount') }}
          />
          <TextField
            label={t('invoices.dueDate')}
            type="datetime-local"
            fullWidth
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            inputProps={{ 'aria-label': t('invoices.dueDate') }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          onClick={() => saveMutation.mutate({ status: 'OPEN' })}
          disabled={isPending}
          color="primary"
        >
          {t('invoices.edit.issue')}
        </Button>
        <Button
          variant="contained"
          onClick={() =>
            saveMutation.mutate({
              ...(parsedAmount != null ? { amount: parsedAmount } : {}),
              ...(dueDate ? { dueDate: new Date(dueDate).toISOString() } : {}),
            })
          }
          disabled={isPending || !canSubmit}
        >
          {t('invoices.edit.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
